// =============================================================================
// Rascunhos — os orçamentos em digitação de quem está logado.
//
// O rascunho já existia (a tela de orçamento salva sozinha 2,5s depois da
// última tecla), mas só dava para reencontrá-lo pelo aviso de "continuar de
// onde parou?" ao abrir /orcamento — ou seja, um de cada vez e sem escolha.
// Quem tinha três orçamentos pela metade não tinha como chegar no segundo.
//
// ⚠️ RASCUNHO NÃO PROMETE NADA A NINGUÉM: não reserva estoque, não entra na
//    fila do Microuniverso e não abre liberação. A tela diz isso, porque a
//    lista se parece com a de Pedidos e a confusão sairia cara — vendedor que
//    acha que o rascunho virou pedido não confirma, e o cliente fica esperando
//    uma mercadoria que ninguém separou.
//
// ⚠️ Só aparecem os DESTE usuário: é o backend que garante (`criado_por`), não
//    a tela.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api, { mensagemDeErro } from '../services/api';
import { CHAVE_LOCAL } from './NovoOrcamento';

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// "há 3 minutos" diz mais do que "24/08/2026 10:14" para quem largou o
// orçamento no meio e voltou. A data completa fica no title, para quem precisa.
function quandoFoi(v) {
  if (!v) return '—';
  const t = new Date(String(v).replace(' ', 'T'));
  if (Number.isNaN(t.getTime())) return String(v);
  const seg = Math.max(0, Math.round((Date.now() - t.getTime()) / 1000));
  if (seg < 60) return 'agora há pouco';
  const min = Math.round(seg / 60);
  if (min < 60) return `há ${min} minuto${min === 1 ? '' : 's'}`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h} hora${h === 1 ? '' : 's'}`;
  const d = Math.round(h / 24);
  if (d < 30) return `há ${d} dia${d === 1 ? '' : 's'}`;
  return t.toLocaleDateString('pt-BR');
}

const dataCompleta = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '');

// O rascunho que ainda não subiu para o servidor — quem estava sem internet, ou
// fechou a aba antes dos 2,5 segundos do autosave. Ele existe só neste
// computador, e omiti-lo seria mentir na lista.
function lerRascunhoLocal() {
  try {
    const cru = localStorage.getItem(CHAVE_LOCAL);
    if (!cru) return null;
    const d = JSON.parse(cru);
    const temConteudo = (d.itens || []).length > 0 || d.cliente_completo;
    return temConteudo ? d : null;
  } catch {
    return null;   // json quebrado não pode derrubar a tela
  }
}

export default function Rascunhos() {
  const navigate = useNavigate();
  const [rascunhos, setRascunhos] = useState([]);
  const [local, setLocal] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get('/api/pedidos/rascunhos');
      const lista = data.rascunhos || [];
      setRascunhos(lista);
      // Só mostra o local se ele não for o mesmo que já está no servidor.
      const l = lerRascunhoLocal();
      setLocal(l && !lista.some((r) => r.uuid === l.uuid) ? l : null);
      setErro('');
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Descartar é decisão explícita e não tem volta — por isso pergunta, e por
  // isso a pergunta diz o que se perde.
  async function descartar(r) {
    const ehLocal = !r.numero;
    const quantos = ehLocal ? (r.itens || []).length : r.itens;
    const cliente = ehLocal ? r.cliente_completo?.razao : r.cliente_razao;
    const conf = await Swal.fire({
      icon: 'warning',
      title: 'Descartar este rascunho?',
      html: `São <strong>${quantos} item(ns)</strong>`
          + `${cliente ? ` para <strong>${cliente}</strong>` : ''}. Isso não tem volta.`,
      showCancelButton: true,
      confirmButtonText: 'Descartar',
      cancelButtonText: 'Manter',
      confirmButtonColor: '#b91c1c',
    });
    if (!conf.isConfirmed) return;

    if (ehLocal) {
      try { localStorage.removeItem(CHAVE_LOCAL); } catch { /* nada a fazer */ }
      setLocal(null);
      return;
    }
    try {
      await api.delete(`/api/pedidos/rascunho/${r.uuid}`);
      // Tira da lista sem recarregar: a resposta já diz que foi.
      setRascunhos((atual) => atual.filter((x) => x.uuid !== r.uuid));
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Não deu para descartar', text: mensagemDeErro(e) });
    }
  }

  const continuar = (r) => navigate(`/orcamento?retomar=${r.numero ? r.uuid : 'local'}`);

  const vazio = !carregando && !rascunhos.length && !local;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="h4 fw-bold mb-1">Rascunhos</h1>
          <p className="text-muted small mb-0">
            Orçamento em digitação, do jeito que você deixou. <strong>Rascunho não promete nada</strong> —
            não reserva estoque nem vai para o Microuniverso até você confirmar.
          </p>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={carregar}
                disabled={carregando}>
          <i className="bi bi-arrow-clockwise me-1" />Atualizar
        </button>
      </div>

      {erro && <div className="alert alert-danger py-2 small">{erro}</div>}

      <div className="card">
        <div className="table-responsive">
          <table className="table table-fnx mb-0">
            <thead>
              <tr>
                <th>Número</th><th>Cliente</th><th>Filial</th>
                <th className="text-end">Itens</th><th className="text-end">Parcial</th>
                <th>Vendedor</th><th>Mexido</th><th className="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando && (
                <tr><td colSpan={8} className="text-center py-4 text-muted">
                  <span className="spinner-border spinner-border-sm me-2" />Carregando…
                </td></tr>
              )}
              {vazio && (
                <tr><td colSpan={8} className="text-center py-5 text-muted">
                  <i className="bi bi-pencil-square display-6 d-block mb-2 opacity-50" />
                  Nenhum orçamento em digitação.
                  <div className="small mt-1">O que você começar e não confirmar aparece aqui.</div>
                </td></tr>
              )}

              {local && (
                <tr>
                  <td>
                    <span className="badge badge-pendente">
                      <i className="bi bi-laptop me-1" />Neste computador
                    </span>
                  </td>
                  <td>{local.cliente_completo?.razao || <span className="text-muted">—</span>}</td>
                  <td><span className="badge badge-filial">{local.filial_venda || '—'}</span></td>
                  <td className="text-end">{(local.itens || []).length}</td>
                  <td className="text-end text-muted">—</td>
                  <td className="text-muted">—</td>
                  <td title={dataCompleta(local.em)}>{quandoFoi(local.em)}</td>
                  <td className="text-end text-nowrap">
                    <button type="button" className="btn btn-sm btn-primary-fnx me-1"
                            onClick={() => continuar(local)}>Continuar</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary"
                            onClick={() => descartar(local)} title="Descartar">
                      <i className="bi bi-trash3" />
                    </button>
                  </td>
                </tr>
              )}

              {rascunhos.map((r) => (
                <tr key={r.uuid}>
                  <td className="fw-semibold">{r.numero}</td>
                  <td>{r.cliente_razao || <span className="text-muted">—</span>}</td>
                  <td><span className="badge badge-filial">{r.filial_venda}</span></td>
                  <td className="text-end">{r.itens}</td>
                  <td className="text-end fw-semibold">{dinheiro(r.valor_liquido)}</td>
                  <td>{r.vendedor_nome || <span className="text-muted">—</span>}</td>
                  <td title={dataCompleta(r.atualizado_em)}>{quandoFoi(r.atualizado_em)}</td>
                  <td className="text-end text-nowrap">
                    <button type="button" className="btn btn-sm btn-primary-fnx me-1"
                            onClick={() => continuar(r)}>Continuar</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary me-1"
                            onClick={() => navigate(`/pedidos/${r.uuid}`)} title="Ver o documento">
                      <i className="bi bi-eye" />
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary"
                            onClick={() => descartar(r)} title="Descartar">
                      <i className="bi bi-trash3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* O "parcial" não é o total do orçamento: é o que dava no momento em que
          o rascunho foi salvo, sem a simulação de entrega. Dizer isso evita a
          pergunta de por que o valor mudou ao confirmar. */}
      {!vazio && !carregando && (
        <p className="text-muted small mt-2 mb-0">
          <i className="bi bi-info-circle me-1" />
          O valor é o parcial do momento em que você parou; o total sai na confirmação.
          O rascunho continua de onde parou, com o mesmo número.
        </p>
      )}
    </>
  );
}
